import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminFormFieldError,
  AdminAlertText,
  AdminFormDropdownChevron,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  ARTICLE_USE_OPTIONS,
  CATEGORY_OPTIONS,
  KB_ROUTE,
  buildSampleKnowledgeArticles,
  findKnowledgeArticle,
} from '../../../data/knowledgeBaseSpec';

const CURRENT_ACTOR = 'Support — ABC Name';

const emptyForm = () => ({
  articleId: '',
  title: '',
  category: '',
  articleUse: '',
  keywords: [],
  topic: '',
  whenToUse: '',
  quickAnswer: '',
  guidanceStepsText: '',
  suggestedReply: '',
  lastUpdated: '—',
  relatedFiles: [],
  articleHistory: [],
  archived: false,
  draft: false,
});

const articleToForm = (article) => ({
  articleId: article.articleId || '',
  title: article.title || '',
  category: article.category || '',
  articleUse: article.articleUse || '',
  keywords: article.keywords || [],
  topic: article.topic || '',
  whenToUse: article.whenToUse || '',
  quickAnswer: article.quickAnswer || '',
  guidanceStepsText: Array.isArray(article.guidanceSteps)
    ? article.guidanceSteps.join('\n')
    : article.guidanceStepsText || '',
  suggestedReply: article.suggestedReply || '',
  lastUpdated: article.lastUpdated || '—',
  relatedFiles: article.relatedFiles || [],
  articleHistory: article.articleHistory || [],
  archived: Boolean(article.archived),
  draft: Boolean(article.draft),
});

const formToArticle = (form, { articleId, lastUpdated, articleHistory, archived, draft }) => {
  const title = form.title.trim();
  const keywords =
    form.keywords?.length > 0
      ? form.keywords
      : title
          .toLowerCase()
          .split(/\s+/)
          .map((w) => w.replace(/[^a-z0-9-]/g, ''))
          .filter((w) => w.length > 2);
  return {
    articleId,
    title,
    category: form.category,
    articleUse: form.articleUse,
    keywords,
    topic: form.topic || title,
    whenToUse: form.whenToUse.trim(),
    quickAnswer: form.quickAnswer.trim(),
    guidanceSteps: form.guidanceStepsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    suggestedReply: form.suggestedReply.trim(),
    lastUpdated,
    relatedFiles: form.relatedFiles || [],
    articleHistory,
    archived: Boolean(archived),
    draft: Boolean(draft),
  };
};

const formatToday = () => {
  const d = new Date();
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const nextArticleId = (articles) => {
  const max = (articles || []).reduce((acc, row) => {
    const match = String(row.articleId || '').match(/KB-(\d+)/i);
    const n = match ? parseInt(match[1], 10) : 0;
    return Math.max(acc, n);
  }, 0);
  return `KB-${String(max + 1).padStart(4, '0')}`;
};

const nextVersionLabel = (history = []) => {
  const max = history.reduce((acc, row) => {
    const match = String(row.version || '').match(/v(\d+)/i);
    const n = match ? parseInt(match[1], 10) : 0;
    return Math.max(acc, n);
  }, 0);
  return `v${max + 1}`;
};

/** Edit/Add locked fields — form-looking read-only inputs (not used on View/Detail). */
const LockedField = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <input
      type="text"
      value={value || '—'}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  </div>
);

/** View/Detail text display — matches other admin Detail pages. */
const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">
        {value || '—'}
      </AdminDetailFieldValue>
    )}
  </div>
);

const KnowledgeBaseDetail = () => {
  const { articleId: articleIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdd = /\/kb\/(?:add|new)\/?$/.test(location.pathname);
  const isViewPath = /\/view\/?$/.test(location.pathname);
  const isEditPath = /\/edit\/?$/.test(location.pathname);
  // Legacy `?mode=edit` and bare `/:articleId` fall through to view unless edit.
  const modeParam = new URLSearchParams(location.search).get('mode');
  const resolvedIsAdd = isAdd;
  const resolvedIsEdit =
    isEditPath || (!resolvedIsAdd && !isViewPath && modeParam === 'edit');
  const resolvedIsView = !resolvedIsAdd && !resolvedIsEdit;

  const articlesFromState = location.state?.articles;
  const [articles] = useState(() =>
    Array.isArray(articlesFromState) ? articlesFromState : buildSampleKnowledgeArticles(),
  );

  const baseline = useMemo(() => {
    if (resolvedIsAdd) return null;
    const fromState = location.state?.article;
    if (fromState?.articleId === decodeURIComponent(String(articleIdParam || ''))) {
      return fromState;
    }
    return findKnowledgeArticle(articles, articleIdParam);
  }, [resolvedIsAdd, location.state?.article, articles, articleIdParam]);

  const [formData, setFormData] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (resolvedIsAdd) {
      setFormData(emptyForm());
    } else if (baseline) {
      setFormData(articleToForm(baseline));
    } else {
      setFormData(emptyForm());
    }
    setFieldErrors({});
    setFormError('');
  }, [baseline, resolvedIsAdd]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Enter a Title.';
    if (!formData.category) errors.category = 'Select Category.';
    if (!formData.articleUse) errors.articleUse = 'Select Article Use.';
    if (!formData.whenToUse.trim()) errors.whenToUse = 'Enter When to Use.';
    if (!formData.quickAnswer.trim()) errors.quickAnswer = 'Enter a Quick Answer.';
    if (!formData.guidanceStepsText.trim()) errors.guidanceStepsText = 'Enter Guidance Steps.';
    if (!formData.suggestedReply.trim()) errors.suggestedReply = 'Enter a Suggested Reply.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const returnToList = (nextArticles, feedback) => {
    navigate(KB_ROUTE, {
      state: { articles: nextArticles, feedback },
    });
  };

  const persistArticle = ({ draft = false, archive = false, summary }) => {
    if (!validate()) {
      setFormError('Please complete the required fields.');
      return;
    }
    setFormError('');

    const nowLabel = formatToday();
    const id = resolvedIsAdd ? nextArticleId(articles) : baseline.articleId;
    const previousHistory = resolvedIsAdd ? [] : formData.articleHistory || [];
    const historyEntry = {
      version: nextVersionLabel(previousHistory),
      updatedBy: CURRENT_ACTOR,
      updatedOn: nowLabel,
      summary:
        summary ||
        (archive
          ? 'Article archived'
          : draft
            ? resolvedIsAdd
              ? 'Draft article saved'
              : 'Draft changes saved'
            : resolvedIsAdd
              ? 'Initial article created'
              : 'Article updated'),
    };
    const articleHistory = [historyEntry, ...previousHistory];
    const saved = formToArticle(formData, {
      articleId: id,
      lastUpdated: nowLabel,
      articleHistory,
      archived: archive || Boolean(formData.archived),
      draft,
    });

    let nextArticles;
    if (resolvedIsAdd) {
      nextArticles = [saved, ...articles];
    } else {
      nextArticles = articles.map((row) => (row.articleId === id ? saved : row));
    }
    returnToList(
      nextArticles,
      archive ? 'Article archived.' : draft ? 'Draft saved.' : 'Changes saved.',
    );
  };

  const handleDraft = () => persistArticle({ draft: true });
  const handleSaveChanges = () => persistArticle({ draft: false });
  const handleArchive = () => persistArticle({ archive: true, summary: 'Article archived' });

  const pageHeading = resolvedIsAdd ? 'Add Article' : 'Edit Article';

  if (!resolvedIsAdd && !baseline) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>
          {resolvedIsView ? 'Knowledge Article Detail' : pageHeading}
        </AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          Article not found.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const displayArticleId = resolvedIsAdd ? 'Assigned after Save' : formData.articleId || '—';
  const guidanceDisplay = formData.guidanceStepsText
    ? formData.guidanceStepsText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((step, idx) => `${idx + 1}. ${step.replace(/^\d+\.\s*/, '')}`)
        .join('\n')
    : '—';

  const relatedFiles = formData.relatedFiles || [];
  const articleHistory = formData.articleHistory || [];
  const relatedFilesSummary =
    relatedFiles.length === 0
      ? 'No related files'
      : relatedFiles.map((file) => `${file.fileName} (${file.fileType})`).join('\n');
  const articleHistorySummary =
    articleHistory.length === 0
      ? 'No article history'
      : articleHistory
          .map((row) => `${row.version} — ${row.updatedBy} · ${row.updatedOn}\n${row.summary}`)
          .join('\n\n');

  if (resolvedIsView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Knowledge Article Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Article ID" value={displayArticleId} mono />
              <TextValue label="Title" value={formData.title} />
              <TextValue label="Category" value={formData.category} />
              <TextValue label="Article Use" value={formData.articleUse} />
              <TextValue label="Last Updated" value={formData.lastUpdated} />
              <div className="sm:col-span-2">
                <TextValue label="When to Use" value={formData.whenToUse} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Quick Answer" value={formData.quickAnswer} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Guidance Steps" value={guidanceDisplay} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Suggested Reply" value={formData.suggestedReply} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Related Files" value={relatedFilesSummary} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Article History" value={articleHistorySummary} />
              </div>
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[25rem] mx-auto w-full min-w-0">
        <div className="mb-6">
          <AdminPageTitle className="!mb-0">{pageHeading}</AdminPageTitle>
        </div>

        <div className="p-4 space-y-6 min-w-0">
          {!resolvedIsAdd ? (
            <LockedField label="Article ID" value={displayArticleId} mono />
          ) : null}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Title <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              placeholder="Enter a Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={adminFormDetailInputClasses(false, Boolean(fieldErrors.title))}
            />
            {fieldErrors.title ? (
              <AdminFormFieldError>{fieldErrors.title}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Category <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full">
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.category))}
              >
                <option value="">Select Category</option>
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {fieldErrors.category ? (
              <AdminFormFieldError>{fieldErrors.category}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Article Use <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <div className="relative w-full">
              <select
                value={formData.articleUse}
                onChange={(e) => handleInputChange('articleUse', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.articleUse))}
              >
                <option value="">Select Article Use</option>
                {ARTICLE_USE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            {fieldErrors.articleUse ? (
              <AdminFormFieldError>{fieldErrors.articleUse}</AdminFormFieldError>
            ) : null}
          </div>

          {!resolvedIsAdd ? (
            <LockedField label="Last Updated" value={formData.lastUpdated} />
          ) : null}

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              When to Use <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <textarea
              placeholder="Enter When to Use"
              value={formData.whenToUse}
              onChange={(e) => handleInputChange('whenToUse', e.target.value)}
              className={adminFormDetailTextareaClasses(false, Boolean(fieldErrors.whenToUse))}
              rows={4}
            />
            {fieldErrors.whenToUse ? (
              <AdminFormFieldError>{fieldErrors.whenToUse}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Quick Answer <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <textarea
              placeholder="Enter a Quick Answer"
              value={formData.quickAnswer}
              onChange={(e) => handleInputChange('quickAnswer', e.target.value)}
              className={adminFormDetailTextareaClasses(false, Boolean(fieldErrors.quickAnswer))}
              rows={4}
            />
            {fieldErrors.quickAnswer ? (
              <AdminFormFieldError>{fieldErrors.quickAnswer}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Guidance Steps <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <textarea
              placeholder="One step per line"
              value={formData.guidanceStepsText}
              onChange={(e) => handleInputChange('guidanceStepsText', e.target.value)}
              className={adminFormDetailTextareaClasses(
                false,
                Boolean(fieldErrors.guidanceStepsText),
              )}
              rows={6}
            />
            {fieldErrors.guidanceStepsText ? (
              <AdminFormFieldError>{fieldErrors.guidanceStepsText}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Suggested Reply <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <textarea
              placeholder="Enter a Suggested Reply"
              value={formData.suggestedReply}
              onChange={(e) => handleInputChange('suggestedReply', e.target.value)}
              className={adminFormDetailTextareaClasses(
                false,
                Boolean(fieldErrors.suggestedReply),
              )}
              rows={5}
            />
            {fieldErrors.suggestedReply ? (
              <AdminFormFieldError>{fieldErrors.suggestedReply}</AdminFormFieldError>
            ) : null}
          </div>

          <div className="space-y-3 w-full min-w-0">
            <AdminDetailFieldLabel className="!mb-0">Related Files</AdminDetailFieldLabel>
            {relatedFiles.length === 0 ? (
              <AdminSupportingText className="text-mediumgray mb-0">
                No related files
              </AdminSupportingText>
            ) : (
              <div className="space-y-2">
                {relatedFiles.map((file) => (
                  <div
                    key={file.fileName}
                    className="rounded-xl border border-lightgray bg-white px-3 py-2 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <AdminSupportingText className="mb-0 !text-offblack truncate">
                        {file.fileName}
                      </AdminSupportingText>
                      <AdminSupportingText className="mb-0 text-mediumgray text-xs">
                        {file.fileType}
                      </AdminSupportingText>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 text-sm font-medium text-primaryblue hover:text-brightblue"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 w-full min-w-0">
            <AdminDetailFieldLabel className="!mb-0">Article History</AdminDetailFieldLabel>
            {articleHistory.length === 0 ? (
              <AdminSupportingText className="text-mediumgray mb-0">
                No article history
              </AdminSupportingText>
            ) : (
              <div className="space-y-2">
                {articleHistory.map((row) => (
                  <div
                    key={`${row.version}-${row.updatedOn}-${row.summary}`}
                    className="rounded-xl border border-lightgray bg-white px-3 py-2 space-y-1"
                  >
                    <AdminSupportingText className="mb-0 !text-offblack font-medium">
                      {row.version}
                    </AdminSupportingText>
                    <AdminSupportingText className="mb-0 text-mediumgray text-xs">
                      {row.updatedBy} · {row.updatedOn}
                    </AdminSupportingText>
                    <AdminSupportingText className="mb-0 !text-offblack">
                      {row.summary}
                    </AdminSupportingText>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            {formError ? <AdminAlertText className="mb-0">{formError}</AdminAlertText> : null}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {!resolvedIsAdd ? (
                <AdminFormSecondaryAction type="button" onClick={handleArchive}>
                  Archive
                </AdminFormSecondaryAction>
              ) : null}
              <AdminFormSecondaryAction type="button" onClick={handleDraft}>
                Draft
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleSaveChanges}>
                Save Changes
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBaseDetail;
